import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Typography,
  CircularProgress,
} from '@mui/material';
import { SkipEvent } from '../domain/SkipEvent';
import { SkipsProcessingJob } from '../domain/SkipsProcessingJob';
import { SkipEventTable } from './SkipEventTable';
import { SkipsProcessingService } from '../services/SkipsProcessingService';

interface SkipEventCardProps {
  skipEvent: SkipEvent;
  onSaved: () => void;
}

export const SkipEventCard: React.FC<SkipEventCardProps> = ({ skipEvent, onSaved }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [, setTriggerRerender] = useState(0);

  const service = new SkipsProcessingService();

  const handleFieldChange = (job: SkipsProcessingJob, field: string, value: any) => {
    switch (field) {
      case 'billablePercent':
        job.setBillablePercent(value);
        break;
      case 'numSamples':
        job.setNumSamples(value);
        break;
      case 'exeNumSamples':
        job.setExeNumSamples(value);
        break;
      case 'exeNumSamplesSkipped':
        job.setExeNumSamplesSkipped(value);
        break;
      case 'previouslySampledExeNum':
        job.setPreviouslySampledExeNum(value);
        break;
      case 'mgrNotesOps':
        job.setMgrNotesOps(value);
        break;
    }

    // Force re-render to update calculated fields and validation
    setTriggerRerender((prev) => prev + 1);
  };

  const handleSave = async () => {
    const validation = skipEvent.validate();

    if (!validation.isValid) {
      // Show confirmation dialog if there are validation errors
      setShowConfirmDialog(true);

      return;
    }

    await performSave();
  };

  const handleConfirmSave = async () => {
    setShowConfirmDialog(false);
    await performSave();
  };

  const performSave = async () => {
    setIsSaving(true);

    try {
      await service.saveSkipEvent(skipEvent);
      onSaved();
    } catch (error) {
      console.error('Failed to save skip event:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const validation = skipEvent.validate();

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          border: '2px solid',
          borderColor: 'divider',
        }}
      >
        <SkipEventTable skipEvent={skipEvent} validation={validation} onFieldChange={handleFieldChange} />

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {!validation.isValid && (
              <Typography variant="body2" color="warning.main">
                ⚠️ There are validation errors. Saving will require confirmation.
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Saving...' : 'Confirm & Save'}
          </Button>
        </Box>
      </Paper>

      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Validation Errors Detected</DialogTitle>
        <DialogContent>
          <DialogContentText>There are validation errors in this skip event:</DialogContentText>
          {validation.errors.archived.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="error">
                Archived Job Errors:
              </Typography>
              <ul>
                {validation.errors.archived.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </Box>
          )}
          {validation.errors.continuation.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="error">
                Continuation Job Errors:
              </Typography>
              <ul>
                {validation.errors.continuation.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </Box>
          )}
          <DialogContentText sx={{ mt: 2 }}>Do you want to proceed with saving anyway?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmSave} color="warning" variant="contained" autoFocus>
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
